import { useState } from "react";
import { Loader2, Search, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { checkDentist, formatApiError } from "@/lib/api";
import { DentistResults } from "./DentistResults";

export const DENTIST_DISCLAIMER =
  "Networks change. Always confirm with the carrier and the dental office before enrolling. This tool is a starting point, not a guarantee of participation.";
export const PROVIDER_DISCLAIMER =
  "Providers can add and drop insurance carriers at any time. Always double-check with the provider’s office to make sure they are currently accepting the insurance before you make a purchase.";

const INITIAL = { last_name: "", first_name: "", location: "32459", state: "FL", practice: "", npi: "" };

const Field = ({ id, label, optional, children }) => (
  <div>
    <Label htmlFor={id} className="text-xs uppercase tracking-[0.18em] text-slate-500">
      {label}
      {optional && <span className="text-slate-400 normal-case tracking-normal"> (optional)</span>}
    </Label>
    {children}
  </div>
);

export const DentistChecker = ({ onAskChristian }) => {
  const [form, setForm] = useState(INITIAL);
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.last_name.trim()) {
      toast.error("Please enter your dentist's last name (or the practice name).");
      return;
    }
    setLoading(true);
    setResult(null);
    const started = Date.now();
    try {
      const data = await checkDentist({ ...form, location: form.location.trim() || "32459" });
      const wait = Math.max(0, 700 - (Date.now() - started));
      setTimeout(() => {
        setResult(data);
        setLoading(false);
      }, wait);
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(false);
    }
  };

  return (
    <div data-testid="dentist-checker">
      <form
        onSubmit={onSubmit}
        data-testid="dentist-check-form"
        className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"
        aria-label="Check whether your dentist accepts our dental plans"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field id="dentist-last" label="Dentist last name or practice">
            <Input
              id="dentist-last"
              data-testid="dentist-input-last"
              value={form.last_name}
              onChange={(e) => update("last_name", e.target.value)}
              placeholder="e.g. Cook or Hammock Bay Dental"
              className="mt-2 h-11"
              autoComplete="off"
              required
            />
          </Field>
          <Field id="dentist-first" label="First name" optional>
            <Input
              id="dentist-first"
              data-testid="dentist-input-first"
              value={form.first_name}
              onChange={(e) => update("first_name", e.target.value)}
              placeholder="Cassidy"
              className="mt-2 h-11"
              autoComplete="off"
            />
          </Field>
          <Field id="dentist-location" label="City or ZIP">
            <Input
              id="dentist-location"
              data-testid="dentist-input-location"
              value={form.location}
              onChange={(e) => update("location", e.target.value)}
              placeholder="32459 or Santa Rosa Beach"
              className="mt-2 h-11"
              autoComplete="postal-code"
            />
          </Field>
          <Field id="dentist-state" label="State">
            <Input
              id="dentist-state"
              data-testid="dentist-input-state"
              value={form.state}
              onChange={(e) => update("state", e.target.value.toUpperCase().slice(0, 2))}
              maxLength={2}
              className="mt-2 h-11 uppercase"
            />
          </Field>
          {showMore && (
            <>
              <Field id="dentist-practice" label="Practice name" optional>
                <Input
                  id="dentist-practice"
                  data-testid="dentist-input-practice"
                  value={form.practice}
                  onChange={(e) => update("practice", e.target.value)}
                  placeholder="Cook Family Dentistry"
                  className="mt-2 h-11"
                />
              </Field>
              <Field id="dentist-npi" label="NPI number" optional>
                <Input
                  id="dentist-npi"
                  data-testid="dentist-input-npi"
                  value={form.npi}
                  onChange={(e) => update("npi", e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-digit NPI"
                  inputMode="numeric"
                  className="mt-2 h-11"
                />
              </Field>
            </>
          )}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            type="submit"
            disabled={loading}
            data-testid="dentist-check-submit"
            className="cta-kinetic inline-flex items-center justify-center rounded-full bg-[var(--brand-secondary)] hover:bg-[var(--brand-secondary-dark)] text-[var(--ink)] hover:text-white text-sm font-semibold px-7 h-12 disabled:opacity-70"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking NCD, Delta, Humana, UnitedHealthcare…
              </>
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" /> Check my dentist
              </>
            )}
          </button>
          <button
            type="button"
            onClick={() => setShowMore((v) => !v)}
            data-testid="dentist-check-more"
            aria-expanded={showMore}
            className="inline-flex items-center text-sm text-slate-600 hover:text-[var(--brand-primary)]"
          >
            {showMore ? "Fewer fields" : "Add practice name or NPI"}
            <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showMore ? "rotate-180" : ""}`} />
          </button>
        </div>
        <p className="mt-4 text-[11px] leading-relaxed text-slate-500" data-testid="dentist-disclaimer">
          {DENTIST_DISCLAIMER} {PROVIDER_DISCLAIMER}
        </p>
      </form>

      {loading && (
        <div
          className="mt-5 rounded-2xl border border-slate-200 bg-white p-6 flex items-center gap-3 text-sm text-slate-600"
          data-testid="dentist-check-loading"
          role="status"
        >
          <Loader2 className="w-4 h-4 animate-spin text-[var(--brand-primary)]" /> Checking NCD, Delta, Humana, UnitedHealthcare…
        </div>
      )}

      {result && !loading && (
        <DentistResults result={result} query={form} onAskChristian={onAskChristian} />
      )}
    </div>
  );
};
